/* Receipt hooks over the accounting-api receipt routes (/v1/expense/receipts). Phase 1 = web
   upload: the bytes go to Azure Blob server-side, the DB holds the metadata + content hash. The
   inbox lists the caller's receipts; upload is content-addressed (re-uploading identical bytes
   returns the existing receipt flagged DUPLICATE); attach links a receipt to a report line. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { expenseFetch } from '@/expense/lib/expense-api';

export type ReceiptMatchState = 'UNMATCHED' | 'MATCHED' | 'DUPLICATE';
export type ReceiptSource = 'UPLOAD' | 'EMAIL' | 'SMS';

export interface Receipt {
  receiptId: number;
  entityId: number;
  ownerUserOid: string;
  reportId: number | null;
  lineId: number | null;
  source: ReceiptSource;
  blobRef: string;
  contentType: string;
  contentSha256: string;
  matchState: ReceiptMatchState;
  uploadedAt: string;
}

const keys = {
  inbox: () => ['expense', 'receipts', 'inbox'] as const,
};

/** The caller's receipt inbox (newest first). */
export function useReceiptInbox() {
  return useQuery({
    queryKey: keys.inbox(),
    queryFn: () => expenseFetch<Receipt[]>('/v1/expense/receipts'),
  });
}

/** Upload a receipt image/PDF (multipart), optionally pre-filing it to a report. */
export function useUploadReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { file: File; reportId?: number }) => {
      const form = new FormData();
      form.append('file', args.file);
      if (args.reportId != null) form.append('reportId', String(args.reportId));
      return expenseFetch<Receipt>('/v1/expense/receipts', {
        method: 'POST',
        body: form,
      });
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['expense', 'receipts'] }),
  });
}

/** Attach a receipt to a report line (marks it MATCHED). */
export function useAttachReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { receiptId: number; lineId: number }) =>
      expenseFetch<Receipt>(`/v1/expense/receipts/${args.receiptId}/attach`, {
        method: 'POST',
        body: JSON.stringify({ lineId: args.lineId }),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['expense', 'receipts'] }),
  });
}
